import React, { useState } from 'react';
import { ReferentialKey } from 'api/referential';
import { useReferential, useReferentialActions } from '../contexts/ReferentialContext';
import { ReferentialCacheStatus } from './ReferentialCacheStatus';

export const ReferentialDevPage: React.FC = () => {
  const { state } = useReferential();
  const {
    initReferentialsAsync,
    fetchReferentialByKeyAsync,
    clearCacheAsync,
    refreshReferentialsAsync
  } = useReferentialActions();

  const [selectedKey, setSelectedKey] = useState<ReferentialKey | ''>('');
  const [jsonView, setJsonView] = useState<'referentials' | 'dropdownItems'>('referentials');

  const handleFetchSingle = () => {
    if (selectedKey) {
      fetchReferentialByKeyAsync(selectedKey as ReferentialKey);
    }
  };

  const referentialKeys = Object.values(ReferentialKey);
  const selectedData = selectedKey ? state[jsonView][selectedKey] : null;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🔧 Gestion des Référentiels (Dev)
        </h1>
        <p className="text-gray-600">
          Interface de développement pour gérer et déboguer les données référentielles
        </p>
      </div>

      {/* Statut du cache */}
      <div className="mb-8">
        <ReferentialCacheStatus showDetails={true} />
      </div>

      {/* Actions globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <button
          onClick={initReferentialsAsync}
          disabled={state.isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Initialiser tous les référentiels
        </button>

        <button
          onClick={refreshReferentialsAsync}
          disabled={state.isLoading}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          Forcer le rechargement
        </button>

        <button
          onClick={clearCacheAsync}
          disabled={state.isLoading}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          Vider le cache
        </button>

        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Recharger la page
        </button>
      </div>

      {/* Statut général */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">État général</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium">Chargement:</span>
            <span className={`ml-2 ${state.isLoading ? 'text-orange-600' : 'text-green-600'}`}>
              {state.isLoading ? 'En cours...' : 'Terminé'}
            </span>
          </div>
          <div>
            <span className="font-medium">Erreur:</span>
            <span className={`ml-2 ${state.error ? 'text-red-600' : 'text-green-600'}`}>
              {state.error || 'Aucune'}
            </span>
          </div>
          <div>
            <span className="font-medium">Référentiels chargés:</span>
            <span className="ml-2 text-blue-600">
              {Object.values(state.referentials).filter(ref => Array.isArray(ref) && ref.length > 0).length} / {referentialKeys.length}
            </span>
          </div>
          <div>
            <span className="font-medium">DropdownItems:</span>
            <span className="ml-2 text-blue-600">
              {Object.values(state.dropdownItems).filter(items => Array.isArray(items) && items.length > 0).length} / {referentialKeys.length}
            </span>
          </div>
        </div>
      </div>

      {/* Actions par référentiel */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Actions par référentiel</h2>

        <div className="flex gap-4 mb-4">
          <select
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value as ReferentialKey | '')}
            className="border rounded px-3 py-2 flex-1"
          >
            <option value="">Sélectionner un référentiel...</option>
            {referentialKeys.map(key => (
              <option key={key} value={key}>
                {key} ({Array.isArray(state.referentials[key]) ? state.referentials[key]?.length || 0 : 0} éléments)
              </option>
            ))}
          </select>

          <button
            onClick={handleFetchSingle}
            disabled={!selectedKey || state.isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Charger ce référentiel
          </button>
        </div>
      </div>

      {/* Vue détaillée */}
      {selectedKey && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              Détails : {selectedKey}
            </h2>

            <div className="flex gap-2">
              <button
                onClick={() => setJsonView('referentials')}
                className={`px-3 py-1 text-sm rounded ${jsonView === 'referentials'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                  }`}
              >
                Données brutes
              </button>
              <button
                onClick={() => setJsonView('dropdownItems')}
                className={`px-3 py-1 text-sm rounded ${jsonView === 'dropdownItems'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                  }`}
              >
                DropdownItems
              </button>
            </div>
          </div>

          {selectedData ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                {Array.isArray(selectedData) ? selectedData.length : 0} élément(s) trouvé(s)
              </div>

              <pre className="bg-gray-50 p-4 rounded overflow-auto max-h-96 text-xs">
                {JSON.stringify(selectedData, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-gray-500 italic">
              Aucune donnée disponible pour ce référentiel
            </div>
          )}
        </div>
      )}

      {/* Liste de tous les référentiels */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-xl font-semibold mb-4">Vue d'ensemble des référentiels</h2>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Référentiel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Données brutes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DropdownItems
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {referentialKeys.map(key => {
                const refData = state.referentials[key];
                const dropdownData = state.dropdownItems[key];

                return (
                  <tr key={key}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {key}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={Array.isArray(refData) && refData.length > 0 ? 'text-green-600' : 'text-red-600'}>
                        {Array.isArray(refData) ? refData.length : 0} éléments
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={Array.isArray(dropdownData) && dropdownData.length > 0 ? 'text-green-600' : 'text-red-600'}>
                        {Array.isArray(dropdownData) ? dropdownData.length : 0} éléments
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => fetchReferentialByKeyAsync(key)}
                        disabled={state.isLoading}
                        className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                      >
                        Recharger
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
